import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, User, Hash, UserPlus } from 'lucide-react';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';
import API_URL from '../api';

function Signup() {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [form, setForm] = useState({ name: '', email: '', password: '', rollNo: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');

  const bg = darkMode ? '#0f0f1a' : '#f0f2f5';
  const card = darkMode ? '#1a1a2e' : '#fff';
  const border = darkMode ? 'rgba(255,255,255,0.08)' : '#e0e0e0';
  const text = darkMode ? '#e0e0e0' : '#333';
  const subtext = darkMode ? '#888' : '#666';
  const inputBg = darkMode ? '#0f0f1a' : '#f8f8f8';

  const handleSignup = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/api/auth/signup`, form);
      setSuccess(res.data.message);
    } catch (err) {
      setError(err.response?.data?.error || 'Signup failed!');
    }
    setLoading(false);
  };

  const inputStyle = (icon) => ({
    background: inputBg, border: `1.5px solid ${border}`, color: text,
    padding: `12px 12px 12px ${icon ? '42px' : '12px'}`,
    borderRadius: '10px', fontSize: '14px', width: '100%',
    marginBottom: 0, outline: 'none', fontFamily: 'inherit'
  });

  const fields = [
    { icon: <User size={16} color="#888" />, label: 'Full Name', placeholder: 'Arshad Shaikh', type: 'text', key: 'name' },
    { icon: <Mail size={16} color="#888" />, label: 'Email Address', placeholder: 'your@email.com', type: 'email', key: 'email' },
    { icon: <Hash size={16} color="#888" />, label: 'Roll Number', placeholder: 'e.g. 2024CS001', type: 'text', key: 'rollNo' },
    { icon: <Lock size={16} color="#888" />, label: 'Password', placeholder: '••••••••', type: 'password', key: 'password' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <style>{`@keyframes fadeInUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }`}</style>
      <div style={{ width: '100%', maxWidth: '420px', animation: 'fadeInUp 0.5s ease' }}>
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <div style={{ background: 'linear-gradient(135deg, #e94560, #c62a47)', borderRadius: '16px', width: '60px', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 15px', fontSize: '28px', boxShadow: '0 8px 25px rgba(233,69,96,0.3)' }}>🎓</div>
          <h1 style={{ color: text, fontSize: '26px', fontWeight: '700', marginBottom: '6px' }}>Create Account</h1>
          <p style={{ color: subtext, fontSize: '14px' }}>Join to register for college events</p>
        </div>
        <div style={{ background: card, borderRadius: '20px', padding: '35px', border: `1px solid ${border}`, boxShadow: darkMode ? '0 20px 60px rgba(0,0,0,0.4)' : '0 20px 60px rgba(0,0,0,0.08)' }}>
          {error && (
            <div style={{ background: darkMode ? 'rgba(233,69,96,0.15)' : '#ffebee', border: '1px solid rgba(233,69,96,0.3)', borderRadius: '10px', padding: '12px 15px', marginBottom: '20px', color: '#e94560', fontSize: '14px' }}>❌ {error}</div>
          )}

          {success ? (
            <div style={{ textAlign: 'center', padding: '10px 0' }}>
              <div style={{ fontSize: '60px', marginBottom: '16px' }}>📧</div>
              <h3 style={{ color: '#4caf50', fontSize: '18px', fontWeight: '700', marginBottom: '10px' }}>Check Your Email!</h3>
              <p style={{ color: text, fontSize: '14px', marginBottom: '8px' }}>{success}</p>
              <p style={{ color: subtext, fontSize: '13px', marginBottom: '20px' }}>
                Click the verification link in your email to activate your account. Check your spam folder if you don't see it!
              </p>
              <div style={{ background: darkMode ? 'rgba(76,175,80,0.1)' : '#e8f5e9', border: '1px solid rgba(76,175,80,0.3)', borderRadius: '10px', padding: '12px', marginBottom: '20px' }}>
                <p style={{ color: '#4caf50', fontSize: '13px', margin: 0 }}>
                  📩 After verifying, come back and <strong>login</strong> to access your account.
                </p>
              </div>
              <Link to="/login" style={{ background: 'linear-gradient(135deg, #e94560, #c62a47)', color: '#fff', padding: '12px 30px', borderRadius: '10px', textDecoration: 'none', fontWeight: '600', fontSize: '14px', display: 'inline-block' }}>
                Go to Login →
              </Link>
            </div>
          ) : (
            <>
              {fields.map((field, i) => (
                <div key={i} style={{ marginBottom: '14px' }}>
                  <label style={{ color: subtext, fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '8px' }}>{field.label}</label>
                  <div style={{ position: 'relative' }}>
                    <div style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }}>{field.icon}</div>
                    <input placeholder={field.placeholder} type={field.type} value={form[field.key]} onChange={e => setForm({ ...form, [field.key]: e.target.value })} onKeyDown={e => e.key === 'Enter' && handleSignup()} style={inputStyle(true)} />
                  </div>
                </div>
              ))}
              <button onClick={handleSignup} disabled={loading} style={{ background: 'linear-gradient(135deg, #e94560, #c62a47)', color: '#fff', width: '100%', padding: '14px', fontSize: '15px', borderRadius: '12px', marginTop: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 4px 15px rgba(233,69,96,0.3)' }}>
                {loading ? '⏳ Creating account...' : <><UserPlus size={17} /> Sign Up</>}
              </button>
            </>
          )}
        </div>
        <p style={{ textAlign: 'center', color: subtext, fontSize: '14px', marginTop: '20px' }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: '#e94560', fontWeight: '600', textDecoration: 'none' }}>Login</Link>
        </p>
      </div>
    </div>
  );
}

export default Signup;